import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  CheckCircle2,
  ClipboardCheck,
  Droplets,
  Factory,
  GraduationCap,
  Leaf,
  Map,
  Waves,
} from 'lucide-react';
import './OurServices.css';

const services = [
  {
    icon: ClipboardCheck,
    number: '01',
    title: 'Environmental and Social Assessments',
    short: 'Assess',
    description: 'Environmental and Social Impact Assessments, Environmental Management Implementation Plans, environmental audits, and strategic environmental and social assessments.',
    details: ['ESIA and SESA studies', 'Environmental audits', 'Management and monitoring plans'],
  },
  {
    icon: Map,
    number: '02',
    title: 'Resettlement Action Plans',
    short: 'Plan',
    description: 'Preparation and implementation support for RAPs, including stakeholder consultation, asset surveys, livelihood impacts, compensation planning, and monitoring.',
    details: ['Socio-economic surveys', 'Asset and livelihood assessment', 'Implementation monitoring'],
  },
  {
    icon: Waves,
    number: '03',
    title: 'Natural and Water Resources Management',
    short: 'Manage',
    description: 'Planning, assessment, and safeguards support for sustainable management of catchments, wetlands, water resources, biodiversity, and other natural assets.',
    details: ['Catchment and wetland planning', 'Biodiversity assessment', 'Resource-management studies'],
  },
  {
    icon: Leaf,
    number: '04',
    title: 'Climate Resilience, Adaptation and Mitigation',
    short: 'Adapt',
    description: 'Climate-risk analysis and practical resilience, adaptation, and mitigation measures for infrastructure, natural systems, and communities.',
    details: ['Climate-risk screening', 'Resilient infrastructure measures', 'Adaptation and mitigation planning'],
  },
  {
    icon: GraduationCap,
    number: '05',
    title: 'Training and Capacity Building',
    short: 'Strengthen',
    description: 'Practical training, manuals, and institutional capacity building in environmental assessment, management, safeguards, and planning.',
    details: ['Tailored technical training', 'Training-manual development', 'Institutional support'],
  },
  {
    icon: Factory,
    number: '06',
    title: 'Cleaner Production',
    short: 'Improve',
    description: 'Cleaner processes, resource efficiency, waste reduction, and low-carbon production solutions, including experience in the cement sector.',
    details: ['Resource-efficiency reviews', 'Waste and emissions reduction', 'Low-carbon production advice'],
  },
  {
    icon: Droplets,
    number: '07',
    title: 'Water Treatment Plant Design and Installation',
    short: 'Design',
    description: 'Design and installation support for drinking-water, wastewater, and faecal-sludge treatment systems suited to local operating conditions.',
    details: ['Treatment-process design', 'System installation support', 'Operational planning'],
  },
];

function OurServices() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Eco Design & Protection Ltd services',
    itemListElement: services.map((service, index) => ({
      '@type': 'Service',
      position: index + 1,
      name: service.title,
      description: service.description,
      provider: { '@type': 'Organization', name: 'Eco Design & Protection Ltd' },
    })),
  };

  return (
    <main className="services-page">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <nav className="services-breadcrumbs" aria-label="Breadcrumb">
        <ol>
          <li><NavLink to="/">Home</NavLink></li>
          <li aria-current="page">Our Services</li>
        </ol>
      </nav>

      <section className="services-hero" aria-labelledby="services-title">
        <div className="services-container services-hero-inner">
          <p className="services-eyebrow">Our services</p>
          <h1 id="services-title">Clear advice for responsible development.</h1>
          <p>
            We combine environmental science, engineering, social safeguards, and local knowledge
            to help projects meet requirements and perform well in practice.
          </p>
          <div className="services-hero-actions">
            <a href="#service-list" className="services-primary-link">View our expertise <ArrowRight size={18} /></a>
            <NavLink to="/contact-us" className="services-secondary-link">Discuss a project</NavLink>
          </div>
        </div>
      </section>

      <section className="services-intro">
        <div className="services-container services-intro-grid">
          <div>
            <p className="services-kicker">One multidisciplinary practice</p>
            <h2>From early assessment to practical implementation</h2>
          </div>
          <div className="services-intro-copy">
            <p>
              Our consultants support roads, renewable energy, gas extraction, hydropower, mining,
              manufacturing, telecommunications, agriculture, irrigation, tourism, and urban infrastructure.
            </p>
            <p>
              Each assignment brings together the specialists needed to produce clear evidence,
              credible stakeholder engagement, and deliverables ready for regulators, lenders, and project teams.
            </p>
          </div>
        </div>
      </section>

      <section id="service-list" className="services-list" aria-labelledby="service-list-title">
        <div className="services-container">
          <div className="services-heading-row">
            <div>
              <p className="services-kicker">What we offer</p>
              <h2 id="service-list-title">Seven connected service areas</h2>
            </div>
            <p>Focused expertise for every stage of responsible project delivery.</p>
          </div>

          <div className="services-grid">
            {services.map(({ icon: Icon, number, title, short, description, details }) => (
              <article className="service-card" key={title}>
                <div className="service-card-top">
                  <span className="service-icon"><Icon size={25} /></span>
                  <span className="service-number">{number}</span>
                </div>
                <p className="service-action">{short}</p>
                <h3>{title}</h3>
                <p className="service-description">{description}</p>
                <ul>
                  {details.map((detail) => (
                    <li key={detail}><CheckCircle2 size={16} /> {detail}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="services-method" aria-labelledby="services-method-title">
        <div className="services-container method-grid">
          <div className="method-copy">
            <p className="services-kicker services-kicker-light">How we work</p>
            <h2 id="services-method-title">Evidence, engagement, and usable results</h2>
            <p>
              We shape the scope around the project, apply national requirements and international
              safeguards, and keep recommendations connected to implementation.
            </p>
          </div>
          <ol className="method-steps">
            <li><span><BarChart3 size={21} /></span><div><strong>Understand</strong><p>Confirm the context, risks, standards, and decisions the study must support.</p></div></li>
            <li><span><ClipboardCheck size={21} /></span><div><strong>Assess and design</strong><p>Collect evidence, consult stakeholders, compare options, and prepare practical measures.</p></div></li>
            <li><span><CheckCircle2 size={21} /></span><div><strong>Deliver and support</strong><p>Provide clear reports, plans, training, and follow-through for implementation.</p></div></li>
          </ol>
        </div>
      </section>

      <section className="services-cta">
        <div className="services-container services-cta-inner">
          <div>
            <p className="services-kicker">Start a conversation</p>
            <h2>Need the right environmental or engineering support for your project?</h2>
          </div>
          <NavLink to="/contact-us" className="services-primary-link">Contact our team <ArrowRight size={18} /></NavLink>
        </div>
      </section>
    </main>
  );
}

export default OurServices;
